import {
  useCreateServiceTag,
  useDeleteServiceTag,
  useServiceTags,
  useUpdateServiceTag,
} from '@my/tms-api/hooks';
import {
  getServiceTagAreaLabel,
  SERVICE_TAG_AREAS,
  type BaseStatusValue,
  type ServiceTag,
  type ServiceTagAreaValue,
} from '@my/tms-api';
import {
  ConfirmDialog,
  ErrorBanner,
  FormSection,
  Loading,
  Switch,
  TextInput,
  showToast,
} from '@my/ui';
import { Check, Edit, Lock, Plus, Trash2, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface ServiceTagsSectionProps {
  /** Owning organisation id; the panel stays disabled while this is null. */
  organisationId: string | null;
}

/**
 * Central Configuration card for an organisation's service tags.
 *
 * Layout mirrors My.Tms.Old: a `+ Add service tag` button in the section
 * header opens an inline editor in a fresh card pinned at the top of the
 * grid; existing rows render as compact cards with name, area chips, a
 * status pill, and an Edit button. Clicking Edit expands the editor inline
 * inside the card so the user keeps spatial context (no modal hop).
 *
 * `isSystemTag` rows hide both Edit and Delete controls — these are seeded
 * by the BE and the delete handler returns 409 anyway, so suppressing the
 * UI keeps the affordance honest.
 *
 * Wire format reminder: `applyToServiceTypes` is the comma-joined string
 * the BE persists. We parse on read, edit as a `Set<ServiceTagAreaValue>`,
 * and re-join on save. The BE canonicalises (orders by enum declaration,
 * dedupes, names-only) so two clients that pick the same areas in any
 * order end up with the same persisted string.
 */
export function ServiceTagsSection({ organisationId }: ServiceTagsSectionProps) {
  const { t } = useTranslation('configuration');
  const listQuery = useServiceTags(organisationId);
  const createMutation = useCreateServiceTag();
  const updateMutation = useUpdateServiceTag();
  const deleteMutation = useDeleteServiceTag();

  // `null` = no editor open; `'new'` = drafting a brand-new tag; a guid
  // = editing the row with that id. Edit renders in-place inside the card;
  // +Add prepends a fresh editor card at the top of the grid.
  const [editingId, setEditingId] = useState<string | 'new' | null>(null);
  const [draft, setDraft] = useState<ServiceTagForm>(emptyForm());
  const [submitted, setSubmitted] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<ServiceTag | null>(null);

  // Ref to whichever editor card is currently mounted (only one at a
  // time). Used to scroll into view so editing the last card doesn't leave
  // the editor below the fold.
  const editorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (editingId === null) return;
    const id = requestAnimationFrame(() => {
      editorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
    return () => cancelAnimationFrame(id);
  }, [editingId]);

  const isBusy = createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

  const startNew = () => {
    setEditingId('new');
    setDraft(emptyForm());
    setSubmitted(false);
  };

  const startEdit = (row: ServiceTag) => {
    setEditingId(row.id);
    setDraft(toForm(row));
    setSubmitted(false);
  };

  const cancel = () => {
    setEditingId(null);
    setDraft(emptyForm());
    setSubmitted(false);
  };

  const handleSave = async () => {
    if (!organisationId || editingId === null) return;
    setSubmitted(true);
    if (!isFormValid(draft)) return;

    // Comma-join in declaration order. The BE canonicalises again on save
    // anyway (so name-case mistakes round-trip cleanly), but emitting a
    // stable order here means the optimistic-cache view matches what the
    // server stores without an extra refetch.
    const applyToServiceTypes = draft.areas.size === 0 ? null : joinAreas(draft.areas);
    const description = draft.description.trim() || null;
    const name = draft.name.trim();

    try {
      if (editingId === 'new') {
        await createMutation.mutateAsync({
          organisationId,
          name,
          description,
          applyToServiceTypes,
          isSystemTag: false,
          status: draft.status,
        });
        showToast({ type: 'success', message: t('serviceTags.added') });
      } else {
        await updateMutation.mutateAsync({
          organisationId,
          body: {
            serviceTagId: editingId,
            name,
            description,
            applyToServiceTypes,
            status: draft.status,
          },
        });
        showToast({ type: 'success', message: t('serviceTags.updated') });
      }
      cancel();
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : t('serviceTags.saveFailed'),
      });
    }
  };

  const requestDelete = (row: ServiceTag) => setPendingDelete(row);

  const confirmDeletePending = async () => {
    const row = pendingDelete;
    if (!row || !organisationId) return;
    try {
      await deleteMutation.mutateAsync({ organisationId, id: row.id });
      showToast({ type: 'success', message: t('serviceTags.removed') });
      if (editingId === row.id) cancel();
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : t('serviceTags.deleteFailed'),
      });
    } finally {
      setPendingDelete(null);
    }
  };

  // Hide the +Add header button while an editor is open — there's no sense
  // starting a second draft when one is already in flight.
  const headerAction =
    listQuery.isSuccess && editingId === null ? (
      <button
        type="button"
        aria-label={t('serviceTags.addAria')}
        onClick={startNew}
        disabled={!organisationId}
        className="flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-xs font-medium text-purple-600 hover:bg-purple-50 disabled:opacity-50"
      >
        <Plus className="h-3.5 w-3.5" />
        {t('serviceTags.add')}
      </button>
    ) : null;

  return (
    <FormSection title={t('serviceTags.title')} columns={1} actions={headerAction}>
      {listQuery.isPending && <Loading label={t('serviceTags.loading')} />}
      {listQuery.isError && (
        <ErrorBanner
          title={t('serviceTags.loadFailed')}
          message={listQuery.error.message}
        />
      )}

      {listQuery.isSuccess && listQuery.data.length === 0 && editingId !== 'new' && (
        <div className="rounded-sm border border-dashed border-slate-300 bg-slate-50 px-3 py-4 text-center">
          <p className="text-sm text-slate-600">{t('serviceTags.empty')}</p>
          <p className="mt-1 text-xs text-slate-500">{t('serviceTags.emptyHint')}</p>
        </div>
      )}

      {listQuery.isSuccess && (listQuery.data.length > 0 || editingId === 'new') && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {/*
            New-tag draft renders in a dedicated card pinned at the top
            of the grid so the +Add click flows the user's eye straight to
            the editor. Existing-row edits render IN-PLACE further down.
          */}
          {editingId === 'new' && (
            <ServiceTagEditorCard
              ref={editorRef}
              draft={draft}
              onChange={setDraft}
              submitted={submitted}
              onSave={handleSave}
              onCancel={cancel}
              isBusy={isBusy}
            />
          )}

          {listQuery.data.map((row) =>
            editingId === row.id ? (
              <ServiceTagEditorCard
                key={row.id}
                ref={editorRef}
                draft={draft}
                onChange={setDraft}
                submitted={submitted}
                onSave={handleSave}
                onCancel={cancel}
                onDelete={row.isSystemTag ? undefined : () => requestDelete(row)}
                isBusy={isBusy}
              />
            ) : (
              <ServiceTagCard
                key={row.id}
                tag={row}
                onEdit={() => startEdit(row)}
                editDisabled={isBusy}
              />
            ),
          )}
        </div>
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        tone="danger"
        title={t('serviceTags.deleteDialog.title')}
        message={
          pendingDelete
            ? t('serviceTags.deleteDialog.message', { name: pendingDelete.name })
            : null
        }
        confirmLabel={t('serviceTags.deleteDialog.confirm')}
        onConfirm={confirmDeletePending}
        onCancel={() => setPendingDelete(null)}
        busy={deleteMutation.isPending}
      />
    </FormSection>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Display card (read-only)
// ────────────────────────────────────────────────────────────────────────────

interface ServiceTagCardProps {
  tag: ServiceTag;
  onEdit: () => void;
  editDisabled: boolean;
}

function ServiceTagCard({ tag, onEdit, editDisabled }: ServiceTagCardProps) {
  const { t } = useTranslation('configuration');
  const areas = parseAreas(tag.applyToServiceTypes);
  // null/empty `applyToServiceTypes` is the "applies to every area"
  // sentinel — surface it as a single neutral chip rather than nothing.
  const showAllChip = areas.length === 0;

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
      <div className="flex items-start justify-between gap-1">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex items-center gap-1">
            {tag.isSystemTag && (
              <Lock
                className="h-3 w-3 shrink-0 text-slate-400"
                aria-label={t('serviceTags.ariaSystem')}
              />
            )}
            <h3 className="truncate text-sm font-semibold text-slate-900">{tag.name}</h3>
          </div>
          <div className="flex flex-wrap gap-1">
            {showAllChip ? (
              <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-700">
                {t('serviceTags.allAreas')}
              </span>
            ) : (
              areas.map((a) => (
                <span
                  key={a}
                  className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-700"
                >
                  {getServiceTagAreaLabel(a)}
                </span>
              ))
            )}
          </div>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          <StatusBadge status={tag.status} />
          {!tag.isSystemTag && (
            <button
              type="button"
              aria-label={t('serviceTags.ariaEdit', { name: tag.name })}
              onClick={onEdit}
              disabled={editDisabled}
              className="flex h-5 w-5 items-center justify-center rounded-sm text-blue-600 hover:bg-blue-50 disabled:opacity-50"
            >
              <Edit className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>
      {tag.description && (
        <p className="mt-2 line-clamp-2 text-xs text-slate-500" title={tag.description}>
          {tag.description}
        </p>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: BaseStatusValue }) {
  const { t } = useTranslation('configuration');
  const isActive = status === 'Active';
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
        isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
      }`}
    >
      {isActive
        ? t('serviceTags.fields.statusActive')
        : t('serviceTags.fields.statusInactive')}
    </span>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Editor card
// ────────────────────────────────────────────────────────────────────────────

interface ServiceTagEditorCardProps {
  draft: ServiceTagForm;
  onChange: (next: ServiceTagForm) => void;
  submitted: boolean;
  onSave: () => void;
  onCancel: () => void;
  onDelete?: () => void;
  isBusy: boolean;
  ref?: React.Ref<HTMLDivElement>;
}

function ServiceTagEditorCard({
  draft,
  onChange,
  submitted,
  onSave,
  onCancel,
  onDelete,
  isBusy,
  ref,
}: ServiceTagEditorCardProps) {
  const { t } = useTranslation('configuration');
  const set = <K extends keyof ServiceTagForm>(key: K, value: ServiceTagForm[K]) =>
    onChange({ ...draft, [key]: value });

  const toggleArea = (area: ServiceTagAreaValue) => {
    const next = new Set(draft.areas);
    if (next.has(area)) next.delete(area);
    else next.add(area);
    set('areas', next);
  };

  const errs = {
    name: !draft.name.trim(),
    descriptionLength: draft.description.length > 256,
    nameLength: draft.name.trim().length > 64,
  };

  return (
    <div
      ref={ref}
      className="space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-3 shadow-sm"
    >
      <TextInput
        label={t('serviceTags.fields.name')}
        name="name"
        autoComplete="off"
        value={draft.name}
        maxLength={64}
        onChange={(e) => set('name', e.target.value)}
        error={submitted && (errs.name || errs.nameLength)}
        errorMessage={
          errs.name
            ? t('serviceTags.validation.nameRequired')
            : errs.nameLength
              ? t('serviceTags.validation.nameLength')
              : ''
        }
      />

      <TextInput
        label={t('serviceTags.fields.description')}
        name="description"
        autoComplete="off"
        value={draft.description}
        maxLength={256}
        onChange={(e) => set('description', e.target.value)}
        error={submitted && errs.descriptionLength}
        errorMessage={
          errs.descriptionLength ? t('serviceTags.validation.descriptionLength') : ''
        }
      />

      <div className="space-y-1">
        <p className="text-xs font-medium text-slate-600">{t('serviceTags.fields.appliesTo')}</p>
        {/*
          "Empty selection means all areas" is a deliberate UX shortcut
          mirroring the BE's null/empty sentinel. Surface it as a muted
          hint paragraph (same `text-xs text-slate-500` rhythm as the
          country line on TaxSchemasSection) so operators don't feel
          obliged to pick every chip.
        */}
        <p className="text-xs text-slate-500">{t('serviceTags.fields.appliesToHint')}</p>
        <div className="flex flex-wrap gap-1.5 pt-1">
          {SERVICE_TAG_AREAS.map((area) => {
            const selected = draft.areas.has(area);
            return (
              <button
                key={area}
                type="button"
                onClick={() => toggleArea(area)}
                className={`rounded-full border px-2 py-0.5 text-[11px] font-medium transition-colors ${
                  selected
                    ? 'border-blue-600 bg-blue-600 text-white'
                    : 'border-slate-300 bg-white text-slate-600 hover:bg-slate-100'
                }`}
              >
                {getServiceTagAreaLabel(area)}
              </button>
            );
          })}
        </div>
      </div>

      <Switch
        label={t('serviceTags.fields.status')}
        name="status"
        checked={draft.status === 'Active'}
        enabledLabel={t('serviceTags.fields.statusActive')}
        disabledLabel={t('serviceTags.fields.statusInactive')}
        onChange={(e) => set('status', e.currentTarget.checked ? 'Active' : 'Inactive')}
      />

      <div className="flex items-center justify-between gap-1 pt-1">
        <div>
          {onDelete && (
            <button
              type="button"
              aria-label={t('serviceTags.ariaDelete')}
              onClick={onDelete}
              disabled={isBusy}
              className="flex h-7 w-7 items-center justify-center rounded-sm text-red-600 hover:bg-red-50 disabled:opacity-50"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            aria-label={t('serviceTags.ariaCancel')}
            onClick={onCancel}
            disabled={isBusy}
            className="flex h-7 w-7 items-center justify-center rounded-sm text-slate-500 hover:bg-slate-200 disabled:opacity-50"
          >
            <X className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            aria-label={t('serviceTags.ariaSave')}
            onClick={onSave}
            disabled={isBusy}
            className="flex h-7 w-7 items-center justify-center rounded-sm text-green-600 hover:bg-green-50 disabled:opacity-50"
          >
            <Check className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Form helpers
// ────────────────────────────────────────────────────────────────────────────

interface ServiceTagForm {
  name: string;
  description: string;
  /** Selected areas. Empty set = "applies to every area" (null on the wire). */
  areas: Set<ServiceTagAreaValue>;
  status: BaseStatusValue;
}

function emptyForm(): ServiceTagForm {
  return {
    name: '',
    description: '',
    areas: new Set<ServiceTagAreaValue>(),
    status: 'Active',
  };
}

function toForm(row: ServiceTag): ServiceTagForm {
  return {
    name: row.name,
    description: row.description ?? '',
    areas: new Set(parseAreas(row.applyToServiceTypes)),
    status: row.status,
  };
}

function isFormValid(form: ServiceTagForm): boolean {
  const name = form.name.trim();
  return name.length > 0 && name.length <= 64 && form.description.length <= 256;
}

/**
 * Parse the BE's comma-joined `applyToServiceTypes` into a typed array,
 * dropping unknowns defensively. The BE rejects unknown names on save, so
 * a token we don't recognise here is almost certainly a stale enum
 * declaration on the FE — log loudly and skip rather than render garbage.
 */
function parseAreas(raw: string | null | undefined): ServiceTagAreaValue[] {
  if (!raw) return [];
  const known = new Set<string>(SERVICE_TAG_AREAS);
  const out: ServiceTagAreaValue[] = [];
  for (const token of raw.split(',')) {
    const t = token.trim();
    if (!t) continue;
    if (known.has(t)) {
      out.push(t as ServiceTagAreaValue);
    } else {
      // Surface the stale token in the console so a dev catches a missed
      // FE-side enum bump quickly. The BE rejects unknown names on save,
      // so we should never actually see this in practice.
      console.warn(`[ServiceTags] Unknown area "${t}" — dropping.`);
    }
  }
  return out;
}

/**
 * Re-emit areas in declaration order so the wire string is stable
 * regardless of the user's click order. Matches what the BE
 * `Canonicalize` helper would produce.
 */
function joinAreas(areas: Set<ServiceTagAreaValue>): string {
  return SERVICE_TAG_AREAS.filter((a) => areas.has(a)).join(',');
}
